import React, { useRef, useEffect, useState } from 'react';
import { Form, FormFieldType } from '../../services/formService';
import { UserDataFieldDto } from '../../types/customUserDataFields';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enGB } from 'date-fns/locale';
import { format as formatDate } from 'date-fns';
import { parseDateFlexible, parseDateTimeFlexible } from '../../utils/dateUtils';
import { calculateAutoSizeFontSize, createCanvasFontMeasurer, createCanvasRichTextMeasurer, calculateFormattedAutoSizeFontSize, DEFAULT_MIN_FONT_SIZE, DEFAULT_LINE_HEIGHT_RATIO, EDITOR_FONT_FAMILY, TextMeasurer, RichTextMeasurer } from '../../utils/pdfHelpers';
import { containsRichTextTags, parseRichText, TextSegment } from '../../utils/richTextParser';

const MOBILE_MAX_FONT_SIZE = 7;
const DESKTOP_MAX_FONT_SIZE = 14;

/**
 * Resolves effective max / min font sizes for a field, combining the field
 * configuration with the mobile/desktop caps. The configured fontSize lives
 * on `pdfConfig.fontSize` (new canonical path) or `fontSize` (legacy).
 */
function resolveAutoSizeBounds(fieldConfig: any, isMobile: boolean): { maxFontSize: number; minFontSize: number } {
  const cap = isMobile ? MOBILE_MAX_FONT_SIZE : DESKTOP_MAX_FONT_SIZE;
  const configuredMax = fieldConfig?.pdfConfig?.fontSize ?? fieldConfig?.fontSize;
  const maxFontSize = configuredMax != null ? Math.min(configuredMax, cap) : cap;
  const configuredMin = fieldConfig?.pdfConfig?.minFontSize ?? fieldConfig?.minFontSize ?? DEFAULT_MIN_FONT_SIZE;
  const minFontSize = Math.min(configuredMin, maxFontSize);
  return { maxFontSize, minFontSize };
}

interface AutoFitOptions {
  width: number;
  height: number;
  maxFontSize: number;
  minFontSize: number;
  measurer: TextMeasurer;
  richMeasurer?: RichTextMeasurer;
}

function computeFitFontSize(text: string, opts: AutoFitOptions): number {
  if (!text || !text.trim()) return opts.maxFontSize;
  if (opts.richMeasurer && containsRichTextTags(text)) {
    return calculateFormattedAutoSizeFontSize({
      segments: parseRichText(text),
      maxWidth: opts.width,
      maxHeight: opts.height,
      maxFontSize: opts.maxFontSize,
      minFontSize: opts.minFontSize,
      measurer: opts.richMeasurer,
    }).baseFontSize;
  }
  return calculateAutoSizeFontSize({
    text,
    maxWidth: opts.width,
    maxHeight: opts.height,
    maxFontSize: opts.maxFontSize,
    minFontSize: opts.minFontSize,
    measurer: opts.measurer,
  }).fontSize;
}

/**
 * Wires shrink-to-fit behaviour onto an <input> or <textarea>: applies once
 * and re-applies on every input event. Never grows the font above maxFontSize.
 */
function attachAutoFitFontSize(
  el: HTMLInputElement | HTMLTextAreaElement,
  opts: AutoFitOptions
): () => void {
  const apply = () => {
    el.style.fontSize = `${computeFitFontSize(el.value || '', opts)}px`;
  };
  apply();
  el.addEventListener('input', apply);
  return apply;
}

export interface PDFPageComponentProps {
  pdfDoc: any;
  pageNumber: number;
  scale: number;
  currentFieldValues: { [key: string]: any };
  onFieldChange: (name: string, value: any, isFromFocus?: boolean) => void;
  onFieldFocus: (name: string) => void;
  onFieldBlur: (name: string, value: any) => void;
  onFieldsFound: (values: { [key: string]: any }) => void;
  form?: Form;
  userDataFields?: UserDataFieldDto[];
}

const PDFPageComponent: React.FC<PDFPageComponentProps> = ({
  pdfDoc,
  pageNumber,
  scale,
  currentFieldValues,
  onFieldChange,
  onFieldFocus,
  onFieldBlur,
  onFieldsFound,
  form,
  userDataFields
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  // Tracks the in-flight PDF.js render task so a re-fire can cancel it.
  const renderTaskRef = useRef<any>(null);
  // Monotonically incremented on every render-effect fire. Each effect captures
  // its own value and bails after any await if a newer render has started.
  // Replaces the earlier isRenderingRef/pageKeyRef pair, which could desync when
  // a cancelled task's finally ran after a newer effect started.
  const renderGenerationRef = useRef(0);
  const [annotationLayerReady, setAnnotationLayerReady] = useState(0);

  // State for MUI DateTimePicker overlay
  const [dateTimePickerState, setDateTimePickerState] = useState<{
    open: boolean;
    fieldName: string | null;
    value: Date | null;
    anchorEl: HTMLElement | null;
  }>({ open: false, fieldName: null, value: null, anchorEl: null });

  // State for MUI DatePicker overlay
  const [datePickerState, setDatePickerState] = useState<{
    open: boolean;
    fieldName: string | null;
    value: Date | null;
    anchorEl: HTMLElement | null;
  }>({ open: false, fieldName: null, value: null, anchorEl: null });

  // Helper to format date in local timezone (not UTC)
  const formatLocalDate = (d: Date): string => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatLocalDateTime = (d: Date): string => {
    const datePart = formatLocalDate(d);
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${datePart}T${hours}:${minutes}`;
  };

  // Helper to normalize date for input values
  const normalizeDateForInput = (value: string, type: 'date' | 'datetime') => {
    if (!value) return '';
    // If it's dd/MM/yyyy
    const ddmmyyyy = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(value);
    if (ddmmyyyy) {
      const result = `${ddmmyyyy[3]}-${ddmmyyyy[2].padStart(2, '0')}-${ddmmyyyy[1].padStart(2, '0')}`;
      return result;
    }
    // If it's dd/MM/yyyy HH:mm
    const ddmmyyyyhhmm = /^(\d{1,2})\/(\d{1,2})\/(\d{4}) (\d{1,2}):(\d{1,2})$/.exec(value);
    if (ddmmyyyyhhmm) {
      const datePart = `${ddmmyyyyhhmm[3]}-${ddmmyyyyhhmm[2].padStart(2, '0')}-${ddmmyyyyhhmm[1].padStart(2, '0')}`;
      const timePart = `${ddmmyyyyhhmm[4].padStart(2, '0')}:${ddmmyyyyhhmm[5].padStart(2, '0')}`;
      const result = type === 'date' ? datePart : `${datePart}T${timePart}`;
      return result;
    }

    // If it's already in yyyy-MM-dd or yyyy-MM-ddTHH:mm:ss format (local ISO without Z)
    const localIsoDate = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (localIsoDate) {
      return value;
    }

    const localIsoDateTime = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::\d{2})?$/.exec(value);
    if (localIsoDateTime) {
      const result = type === 'date' ? value.substring(0, 10) : value.substring(0, 16);
      return result;
    }

    try {
      const d = new Date(value);
      if (!isNaN(d.getTime())) {
        // Check if input was UTC (ends with Z) - if so, the Date object already converted to local
        // Format using LOCAL time, not UTC (toISOString would give UTC)
        const result = type === 'date' ? formatLocalDate(d) : formatLocalDateTime(d);
        return result;
      }
    } catch (e) {
      console.error(`[PDFPageComponent] normalizeDateForInput - Parse error:`, e);
    }
    return value;
  };

  /**
   * Converts parsed TextSegments into an HTML string with styled spans.
   */
  const renderRichTextToHTML = (segments: TextSegment[], baseFontSize: number): string => {
    const escapeHTML = (str: string) =>
      str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

    return segments
      .map(seg => {
        const text = escapeHTML(seg.text).replace(/\n/g, '<br/>');
        const styles: string[] = [];
        if (seg.style.bold) styles.push('font-weight:bold');
        if (seg.style.sizeOffset !== 0) {
          const size = Math.max(1, baseFontSize + seg.style.sizeOffset);
          styles.push(`font-size:${size}px`);
        }
        return styles.length > 0
          ? `<span style="${styles.join(';')}">${text}</span>`
          : text;
      })
      .join('');
  };

  const createInteractiveField = (
    field: any,
    container: HTMLElement,
    index: number,
    initValuesAccumulator: any
  ) => {
    const rect = field.rect;
    if (!rect || rect.length < 4) return;

    // PDF coordinates to viewport coordinates
    const [x1, y1, x2, y2] = rect;
    const width = (x2 - x1) * scale;
    const height = (y2 - y1) * scale;
    const left = x1 * scale;
    const top = (container.clientHeight - y2 * scale); // Flip Y coordinate

    // Determine initial value
    let initialValue: any = '';
    if (field.checkBox) {
      const raw = field.fieldValue || field.buttonValue;
      initialValue = raw === 'Yes' || raw === true || raw === 'On';
    } else {
      initialValue = field.fieldValue || field.buttonValue || '';
    }

    if (field.fieldName) {
      initValuesAccumulator[field.fieldName] = initialValue;
      if (initialValue === '' || initialValue === undefined || initialValue === null) {
        console.log(`[UDF_DEBUG] PDFPageComponent field="${field.fieldName}" has empty/null value from PDF annotation`);
      }
    }

    // Use current value from props if available (user edit), else PDF default
    const currentValue = field.fieldName && currentFieldValues[field.fieldName] !== undefined
      ? currentFieldValues[field.fieldName]
      : initialValue;
    if (field.fieldName && currentFieldValues[field.fieldName] !== undefined && currentFieldValues[field.fieldName] !== initialValue) {
      console.log(`[UDF_DEBUG] PDFPageComponent field="${field.fieldName}" currentValue="${currentValue}" vs pdfValue="${initialValue}"`);
    }

    if (field.checkBox) {
      const checkboxContainer = document.createElement('div');
      checkboxContainer.style.position = 'absolute';
      checkboxContainer.style.left = `${left}px`;
      checkboxContainer.style.top = `${top}px`;
      checkboxContainer.style.width = `${Math.min(width, 20)}px`;
      checkboxContainer.style.height = `${Math.min(height, 20)}px`;
      checkboxContainer.style.zIndex = '10';

      const checkmark = document.createElement('div');
      checkmark.textContent = '✓';
      checkmark.style.position = 'absolute';
      checkmark.style.top = '50%';
      checkmark.style.left = '50%';
      checkmark.style.transform = 'translate(-50%, -50%)';
      checkmark.style.fontSize = `${Math.min(height, 20) * 0.8}px`;
      checkmark.style.lineHeight = '1';
      checkmark.style.fontWeight = 'bold';
      checkmark.style.color = '#000';
      checkmark.style.pointerEvents = 'none';
      checkmark.style.userSelect = 'none';
      checkmark.style.display = currentValue ? 'block' : 'none';

      const checkboxInput = document.createElement('input');
      checkboxInput.type = 'checkbox';
      checkboxInput.checked = Boolean(currentValue);
      checkboxInput.style.position = 'absolute';
      checkboxInput.style.width = '100%';
      checkboxInput.style.height = '100%';
      checkboxInput.style.margin = '0';
      checkboxInput.style.padding = '0';
      checkboxInput.style.opacity = '0';
      checkboxInput.style.cursor = 'pointer';
      if (field.fieldName) {
        checkboxInput.setAttribute('data-field-name', field.fieldName);
      }
      checkboxInput.setAttribute('data-field-left', String(left));
      checkboxInput.setAttribute('data-field-top', String(top));
      checkboxInput.setAttribute('data-field-width', String(Math.min(width, 20)));
      checkboxInput.setAttribute('data-field-is-checkbox', 'true');

      checkboxInput.addEventListener('change', (e) => {
        const target = e.target as HTMLInputElement;
        checkmark.style.display = target.checked ? 'block' : 'none';
        if (field.fieldName) {
          onFieldChange(field.fieldName, target.checked);
        }
      });

      checkboxContainer.appendChild(checkmark);
      checkboxContainer.appendChild(checkboxInput);
      container.appendChild(checkboxContainer);
    } else {
      let inputElement: HTMLInputElement | HTMLTextAreaElement;

      // Determine field type from form metadata if available
      let fieldType: any;
      if (form && field.fieldName) {
        for (const section of form.formSections || []) {
          const found = section.formFields?.find(f => f.code === field.fieldName);
          if (found) {
            fieldType = found.fieldType;
            break;
          }
        }
      }

      const isDateField = fieldType === FormFieldType.Date || fieldType === 'Date' || fieldType === 7;
      const isDateTimeField = fieldType === FormFieldType.DateTime || fieldType === 'DateTime' || fieldType === 8;

      if (field.multiLine) {
        inputElement = document.createElement('textarea');
        inputElement.style.resize = 'none';
        inputElement.style.overflow = 'hidden';
        inputElement.style.fontFamily = EDITOR_FONT_FAMILY;
        inputElement.style.lineHeight = String(DEFAULT_LINE_HEIGHT_RATIO);

        // Store field config for auto-sizing
        let fieldConfig: any = {};
        if (form && field.fieldName) {
          for (const section of form.formSections || []) {
            const found = section.formFields?.find(f => f.code === field.fieldName);
            if (found?.configurationJson) {
              try {
                fieldConfig = JSON.parse(found.configurationJson);
              } catch {}
            }
          }
        }
        (inputElement as any)._fieldConfig = fieldConfig;
      } else if (isDateField) {
        // Date-only fields use MUI DatePicker for consistency
        // Create a wrapper to hold input and calendar icon
        const wrapper = document.createElement('div');
        wrapper.style.position = 'relative';
        wrapper.style.display = 'flex';
        wrapper.style.alignItems = 'center';

        inputElement = document.createElement('input');
        (inputElement as HTMLInputElement).type = 'text';
        (inputElement as HTMLInputElement).readOnly = true;
        inputElement.setAttribute('data-field-type', 'date');
        inputElement.style.cursor = 'pointer';

        // Format display value
        if (currentValue) {
          const dateVal = typeof currentValue === 'string' ? parseDateFlexible(currentValue) : currentValue;
          if (dateVal && !isNaN(dateVal.getTime())) {
            inputElement.value = formatDate(dateVal, 'dd/MM/yyyy', { locale: enGB });
          }
        }

        // Create calendar icon
        const calendarIcon = document.createElement('span');
        calendarIcon.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>`;
        calendarIcon.style.position = 'absolute';
        calendarIcon.style.right = '2px';
        calendarIcon.style.top = '50%';
        calendarIcon.style.transform = 'translateY(-50%)';
        calendarIcon.style.cursor = 'pointer';
        calendarIcon.style.color = '#666';
        calendarIcon.style.display = 'flex';
        calendarIcon.style.alignItems = 'center';
        calendarIcon.style.pointerEvents = 'auto';
        calendarIcon.style.backgroundColor = 'white';

        // Open MUI DatePicker on click (both input and icon)
        const openDatePicker = (e: Event) => {
          e.preventDefault();
          e.stopPropagation();
          const dateValue = currentValue
            ? (typeof currentValue === 'string' ? parseDateFlexible(currentValue) : currentValue)
            : null;
          setDatePickerState({
            open: true,
            fieldName: field.fieldName,
            value: dateValue,
            anchorEl: inputElement as HTMLElement
          });
        };

        inputElement.addEventListener('click', openDatePicker);
        calendarIcon.addEventListener('click', openDatePicker);

        // Store wrapper reference for positioning
        (inputElement as any)._wrapper = wrapper;
        (inputElement as any)._calendarIcon = calendarIcon;
      } else if (isDateTimeField) {
        // DateTime fields use MUI DateTimePicker for 15-minute steps
        // Create a wrapper to hold input and calendar icon
        const wrapper = document.createElement('div');
        wrapper.style.position = 'relative';
        wrapper.style.display = 'flex';
        wrapper.style.alignItems = 'center';

        inputElement = document.createElement('input');
        (inputElement as HTMLInputElement).type = 'text';
        (inputElement as HTMLInputElement).readOnly = true;
        inputElement.setAttribute('data-field-type', 'datetime');
        inputElement.style.cursor = 'pointer';

        // Format display value
        if (currentValue) {
          const dateVal = typeof currentValue === 'string' ? parseDateTimeFlexible(currentValue) : currentValue;
          if (dateVal && !isNaN(dateVal.getTime())) {
            inputElement.value = formatDate(dateVal, 'dd/MM/yyyy h:mm a', { locale: enGB });
          } else {
            inputElement.value = currentValue;
          }
        }

        // Create calendar icon
        const calendarIcon = document.createElement('span');
        calendarIcon.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>`;
        calendarIcon.style.position = 'absolute';
        calendarIcon.style.right = '2px';
        calendarIcon.style.top = '50%';
        calendarIcon.style.transform = 'translateY(-50%)';
        calendarIcon.style.cursor = 'pointer';
        calendarIcon.style.color = '#666';
        calendarIcon.style.display = 'flex';
        calendarIcon.style.alignItems = 'center';
        calendarIcon.style.pointerEvents = 'auto';
        calendarIcon.style.backgroundColor = 'white';

        // Open MUI DateTimePicker on click (both input and icon)
        const openDateTimePicker = (e: Event) => {
          e.preventDefault();
          e.stopPropagation();
          const dateValue = currentValue
            ? (typeof currentValue === 'string' ? parseDateTimeFlexible(currentValue) : currentValue)
            : null;
          setDateTimePickerState({
            open: true,
            fieldName: field.fieldName,
            value: dateValue,
            anchorEl: inputElement as HTMLElement
          });
        };

        inputElement.addEventListener('click', openDateTimePicker);
        calendarIcon.addEventListener('click', openDateTimePicker);

        // Store wrapper reference for positioning
        (inputElement as any)._wrapper = wrapper;
        (inputElement as any)._calendarIcon = calendarIcon;
      } else {
        inputElement = document.createElement('input');
        (inputElement as HTMLInputElement).type = 'text';
      }

      if (field.fieldName) {
        inputElement.setAttribute('data-field-name', field.fieldName);
      }
      inputElement.setAttribute('data-field-left', String(left));
      inputElement.setAttribute('data-field-top', String(top));
      inputElement.setAttribute('data-field-width', String(width));

      if (!(isDateField || isDateTimeField)) {
        inputElement.value = currentValue || '';
      }
      inputElement.style.width = `${width}px`;
      inputElement.style.height = `${height}px`;

      // Use smaller font on mobile to fit fields, original scaling on desktop
      const isMobile = window.innerWidth <= 768;

      // For textareas, use auto-sizing based on content
      if (field.multiLine) {
        const fieldConfig = (inputElement as any)._fieldConfig || {};
        const { maxFontSize, minFontSize } = resolveAutoSizeBounds(fieldConfig, isMobile);
        const measurer = createCanvasFontMeasurer(EDITOR_FONT_FAMILY);
        const richMeasurer = createCanvasRichTextMeasurer(EDITOR_FONT_FAMILY);

        attachAutoFitFontSize(inputElement, {
          width,
          height,
          maxFontSize,
          minFontSize,
          measurer,
          richMeasurer,
        });

        // Rich text display div: shown when not editing, hides raw tags
        const displayDiv = document.createElement('div');
        displayDiv.style.position = 'absolute';
        displayDiv.style.left = `${left}px`;
        displayDiv.style.top = `${top}px`;
        displayDiv.style.width = `${width}px`;
        displayDiv.style.height = `${height}px`;
        displayDiv.style.border = '1px solid #ccc';
        displayDiv.style.backgroundColor = 'white';
        displayDiv.style.zIndex = '11';
        displayDiv.style.padding = '2px';
        displayDiv.style.boxSizing = 'border-box';
        displayDiv.style.overflow = 'hidden';
        displayDiv.style.fontFamily = EDITOR_FONT_FAMILY;
        displayDiv.style.lineHeight = String(DEFAULT_LINE_HEIGHT_RATIO);
        displayDiv.style.cursor = 'text';
        displayDiv.style.whiteSpace = 'pre-wrap';
        displayDiv.style.wordBreak = 'break-word';
        displayDiv.setAttribute('data-rich-display', field.fieldName || '');

        // Initially populate if value has rich text
        const initialText = currentValue || '';
        if (containsRichTextTags(initialText)) {
          const segments = parseRichText(initialText);
          const richResult = calculateFormattedAutoSizeFontSize({
            segments,
            maxWidth: width,
            maxHeight: height,
            maxFontSize,
            minFontSize,
            measurer: richMeasurer
          });
          displayDiv.style.fontSize = `${richResult.baseFontSize}px`;
          displayDiv.innerHTML = renderRichTextToHTML(segments, richResult.baseFontSize);
          displayDiv.style.display = 'block';
          inputElement.style.display = 'none';
        } else {
          displayDiv.style.display = 'none';
        }

        // Click display div -> switch to textarea editing
        displayDiv.addEventListener('click', () => {
          displayDiv.style.display = 'none';
          inputElement.style.display = 'block';
          (inputElement as HTMLTextAreaElement).focus();
        });

        // Store reference for focus/blur handlers
        (inputElement as any)._richDisplayDiv = displayDiv;
      } else {
        // Single-line fields (text, dropdown, date, datetime): shrink-to-fit
        // the configured max font size down to minFontSize based on content.
        let fieldConfig: any = {};
        if (form && field.fieldName) {
          for (const section of form.formSections || []) {
            const found = section.formFields?.find(f => f.code === field.fieldName);
            if (found?.configurationJson) {
              try {
                fieldConfig = JSON.parse(found.configurationJson);
              } catch {}
            }
          }
        }
        const { maxFontSize, minFontSize } = resolveAutoSizeBounds(fieldConfig, isMobile);
        const measurer = createCanvasFontMeasurer(EDITOR_FONT_FAMILY);
        attachAutoFitFontSize(inputElement, {
          width,
          height,
          maxFontSize,
          minFontSize,
          measurer,
        });
      }

      inputElement.style.position = 'absolute';
      inputElement.style.left = `${left}px`;
      inputElement.style.top = `${top}px`;
      inputElement.style.border = '1px solid #ccc';
      inputElement.style.backgroundColor = 'white';
      inputElement.style.zIndex = '10';
      inputElement.style.padding = '2px';
      inputElement.style.boxSizing = 'border-box';

      inputElement.addEventListener('focus', (e) => {
        if (field.fieldName) {
          onFieldFocus(field.fieldName);
        }
      });

      inputElement.addEventListener('blur', (e) => {
        const target = e.target as HTMLInputElement | HTMLTextAreaElement;
        if (field.fieldName) {
          onFieldBlur(field.fieldName, target.value);
        }
        // For multiline fields with rich text, switch back to display div
        const richDiv = (inputElement as any)._richDisplayDiv as HTMLDivElement | undefined;
        if (richDiv && target instanceof HTMLTextAreaElement) {
          const text = target.value || '';
          if (containsRichTextTags(text)) {
            const fieldConfig = (inputElement as any)._fieldConfig || {};
            const { maxFontSize: mfs, minFontSize: minFs } = resolveAutoSizeBounds(fieldConfig, window.innerWidth <= 768);
            const rm = createCanvasRichTextMeasurer(EDITOR_FONT_FAMILY);
            const segments = parseRichText(text);
            const result = calculateFormattedAutoSizeFontSize({
              segments,
              maxWidth: width,
              maxHeight: height,
              maxFontSize: mfs,
              minFontSize: minFs,
              measurer: rm
            });
            richDiv.style.fontSize = `${result.baseFontSize}px`;
            richDiv.innerHTML = renderRichTextToHTML(segments, result.baseFontSize);
            richDiv.style.display = 'block';
            inputElement.style.display = 'none';
          } else {
            richDiv.style.display = 'none';
            inputElement.style.display = 'block';
          }
        }
      });

      // Note: For multiline, input listener for auto-sizing is added above
      inputElement.addEventListener('input', (e) => {
        const target = e.target as HTMLInputElement | HTMLTextAreaElement;
        if (field.fieldName) {
          onFieldChange(field.fieldName, target.value);
        }
      });

      // For date/datetime fields, use wrapper with calendar icon
      if ((inputElement as any)._wrapper && (inputElement as any)._calendarIcon) {
        const wrapper = (inputElement as any)._wrapper as HTMLDivElement;
        const calendarIcon = (inputElement as any)._calendarIcon as HTMLSpanElement;

        // Set wrapper positioning (copy styles from input)
        wrapper.style.position = 'absolute';
        wrapper.style.left = `${left}px`;
        wrapper.style.top = `${top}px`;
        wrapper.style.width = `${width}px`;
        wrapper.style.height = `${height}px`;
        wrapper.style.zIndex = '10';

        // Reset input positioning (it's now relative to wrapper)
        inputElement.style.position = 'static';
        inputElement.style.left = 'auto';
        inputElement.style.top = 'auto';
        inputElement.style.width = '100%';
        inputElement.style.height = '100%';
        inputElement.style.zIndex = 'auto';
        inputElement.style.paddingRight = '20px'; // Make room for calendar icon

        // Ensure icon is visible above input
        calendarIcon.style.zIndex = '11';

        wrapper.appendChild(inputElement);
        wrapper.appendChild(calendarIcon);
        container.appendChild(wrapper);
      } else {
        container.appendChild(inputElement);
        // Append rich text display div if present
        const richDiv = (inputElement as any)._richDisplayDiv as HTMLDivElement | undefined;
        if (richDiv) {
          container.appendChild(richDiv);
        }
      }
    }

    // Load Default Button Logic (Shared for both input and checkbox)
    let userDataProperty: string | null = null;
    if (form && field.fieldName) {
      for (const section of form.formSections || []) {
        const found = section.formFields?.find(f => f.code === field.fieldName);
        if (found) {
          try {
            const config = found.configurationJson ? JSON.parse(found.configurationJson) : {};
            console.log(`[LoadDefault] createInteractiveField: field="${field.fieldName}" parameterValue=`, config?.parameterValue);
            if (config?.parameterValue?.source === 'UserDataField') {
              userDataProperty = config.parameterValue.property;
            }
          } catch (e) { }
          break;
        }
      }
    }
    console.log(`[LoadDefault] createInteractiveField: field="${field.fieldName}" userDataProperty="${userDataProperty}" userDataFields count=${userDataFields?.length ?? 'undefined'}`);

    if (userDataProperty && userDataFields) {
      const matchingVal = userDataFields.find((f: UserDataFieldDto) => f.businessDataField?.code === userDataProperty)?.value;
      console.log(`[LoadDefault] createInteractiveField: field="${field.fieldName}" looking for code="${userDataProperty}" matchingVal=`, matchingVal, 'available codes=', userDataFields.map(f => f.businessDataField?.code));
      if (matchingVal !== undefined && matchingVal !== null) {
        const loadBtn = document.createElement('button');
        loadBtn.setAttribute('data-load-default-btn', field.fieldName || '');
        loadBtn.innerHTML = 'Load Default';
        loadBtn.title = `Load Default: ${matchingVal}`;
        loadBtn.style.position = 'absolute';
        const btnLeft = field.checkBox ? left + Math.min(width, 20) + 10 : left + width + 10;
        loadBtn.style.left = `${btnLeft}px`;
        loadBtn.style.top = `${top}px`;
        loadBtn.style.padding = '12px 15px';
        loadBtn.style.fontSize = '12px';
        loadBtn.style.whiteSpace = 'nowrap';
        loadBtn.style.cursor = 'pointer';
        loadBtn.style.zIndex = '11';
        loadBtn.style.backgroundColor = '#f0f0f0';
        loadBtn.style.border = '1px solid #ccc';
        loadBtn.style.borderRadius = '3px';

        loadBtn.onclick = (e) => {
          e.preventDefault();
          if (field.fieldName) {
            let valToSet: any = matchingVal;
            if (field.checkBox) {
              valToSet = matchingVal === 'true' || matchingVal === 'True' || matchingVal === 'Yes' || matchingVal === 'On';
            }
            onFieldChange(field.fieldName, valToSet);
          }
        };
        container.appendChild(loadBtn);
      }
    }
  };

  useEffect(() => {
    const myGeneration = ++renderGenerationRef.current;
    let localRenderTask: any = null;

    const isStale = () => renderGenerationRef.current !== myGeneration;

    const renderPage = async () => {
      if (!canvasRef.current || !wrapperRef.current || !pdfDoc) return;

      // Cancel any in-flight render and AWAIT its promise before touching the
      // canvas. PDF.js's cancel() is not synchronous: the task's draw commands
      // can keep flushing until it rejects with RenderingCancelledException.
      // Without awaiting, two renders can interleave on the same canvas and
      // leave mirrored/garbled output (the root cause of the backwards-pdf bug).
      const prior = renderTaskRef.current;
      if (prior) {
        try { prior.cancel(); } catch { /* no-op */ }
        try { await prior.promise; } catch { /* cancel rejection expected */ }
      }

      if (isStale()) return;

      const canvas = canvasRef.current;
      if (!canvas || !canvas.isConnected) return;
      const context = canvas.getContext('2d');
      if (!context) return;

      const page = await pdfDoc.getPage(pageNumber);
      if (isStale() || !canvasRef.current || !canvasRef.current.isConnected) return;

      const viewport = page.getViewport({ scale });
      if (isStale() || !canvasRef.current || !canvasRef.current.isConnected) return;

      // Setting width/height resets the 2d context state (including any
      // transform), so once we get here the canvas is in a known clean state
      // for the new render.
      canvas.height = viewport.height;
      canvas.width = viewport.width;
      wrapperRef.current.style.width = `${viewport.width}px`;
      wrapperRef.current.style.height = `${viewport.height}px`;

      localRenderTask = page.render({ canvasContext: context, viewport });
      renderTaskRef.current = localRenderTask;

      try {
        await localRenderTask.promise;
      } catch (error: any) {
        if (error?.name === 'RenderingCancelledException') return;
        throw error;
      } finally {
        if (renderTaskRef.current === localRenderTask) {
          renderTaskRef.current = null;
        }
      }

      if (isStale()) return;

      const annotations = await page.getAnnotations();
      if (isStale() || !wrapperRef.current) return;

      const formFields = annotations.filter((a: any) => a.subtype === 'Widget');

      const oldLayers = wrapperRef.current.querySelectorAll('.annotationLayer');
      oldLayers.forEach(l => l.remove());

      const annotationLayer = document.createElement('div');
      annotationLayer.className = 'annotationLayer';
      annotationLayer.style.position = 'absolute';
      annotationLayer.style.left = '0';
      annotationLayer.style.top = '0';
      annotationLayer.style.width = `${viewport.width}px`;
      annotationLayer.style.height = `${viewport.height}px`;
      wrapperRef.current.appendChild(annotationLayer);

      const pageInitialValues: { [key: string]: any } = {};
      formFields.forEach((field: any, index: number) => {
        createInteractiveField(field, annotationLayer, index, pageInitialValues);
      });

      onFieldsFound(pageInitialValues);
      setAnnotationLayerReady(prev => prev + 1);
    };

    renderPage().catch((error: any) => {
      if (error?.name !== 'RenderingCancelledException') {
        console.error('Error rendering page ' + pageNumber, error);
      }
    });

    return () => {
      // Bump the generation so any in-flight awaits bail on their next check.
      renderGenerationRef.current++;
      if (localRenderTask) {
        try { localRenderTask.cancel(); } catch { /* no-op */ }
      }
    };
  }, [pdfDoc, pageNumber, scale]);

  // Update field values in DOM when currentFieldValues changes without re-rendering the whole page
  useEffect(() => {
    if (!wrapperRef.current) return;

    // Find all interactive elements with data-field-name
    const elements = wrapperRef.current.querySelectorAll('[data-field-name]');
    elements.forEach(el => {
      const fieldName = el.getAttribute('data-field-name');
      if (!fieldName) return;

      const newValue = currentFieldValues[fieldName];
      if (newValue === undefined) return;

      if (el instanceof HTMLInputElement) {
        if (el.type === 'checkbox') {
          const isChecked = Boolean(newValue);
          if (el.checked !== isChecked) {
            el.checked = isChecked;
            const checkmark = el.parentElement?.querySelector('div');
            if (checkmark) {
              (checkmark as HTMLElement).style.display = isChecked ? 'block' : 'none';
            }
          }
        } else {
          const isDateField = el.getAttribute('data-field-type') === 'date';
          const isDateTimeField = el.getAttribute('data-field-type') === 'datetime';

          if (isDateField) {
            // Date fields display formatted value
            const dateVal = typeof newValue === 'string' ? parseDateFlexible(newValue) : newValue;
            if (dateVal && !isNaN(dateVal.getTime())) {
              const formatted = formatDate(dateVal, 'dd/MM/yyyy', { locale: enGB });
              if (el.value !== formatted) {
                el.value = formatted;
              }
            }
          } else if (isDateTimeField) {
            // DateTime fields display formatted value
            const dateVal = typeof newValue === 'string' ? parseDateTimeFlexible(newValue) : newValue;
            if (dateVal && !isNaN(dateVal.getTime())) {
              const formatted = formatDate(dateVal, 'dd/MM/yyyy h:mm a', { locale: enGB });
              if (el.value !== formatted) {
                el.value = formatted;
              }
            }
          } else {
            const stringValue = String(newValue);
            if (el.value !== stringValue) {
              const hasFocus = document.activeElement === el;
              const cursorStart = hasFocus ? el.selectionStart : null;
              const cursorEnd = hasFocus ? el.selectionEnd : null;

              el.value = stringValue;

              if (hasFocus && stringValue.includes('{{')) {
                el.select();
              } else if (hasFocus && cursorStart !== null && cursorEnd !== null) {
                const newLength = stringValue.length;
                if (newLength > 0 && cursorStart <= newLength) {
                  el.setSelectionRange(Math.min(cursorStart, newLength), Math.min(cursorEnd, newLength));
                }
              }
            }
          }
        }
      } else if (el instanceof HTMLTextAreaElement) {
        const stringValue = String(newValue);
        if (el.value !== stringValue) {
          const hasFocus = document.activeElement === el;
          const cursorStart = hasFocus ? el.selectionStart : null;
          const cursorEnd = hasFocus ? el.selectionEnd : null;

          el.value = stringValue;

          // Trigger auto-size recalculation
          el.dispatchEvent(new Event('input', { bubbles: true }));

          if (hasFocus && stringValue.includes('{{')) {
            el.select();
          } else if (hasFocus && cursorStart !== null && cursorEnd !== null) {
            const newLength = stringValue.length;
            if (newLength > 0 && cursorStart <= newLength) {
              el.setSelectionRange(Math.min(cursorStart, newLength), Math.min(cursorEnd, newLength));
            }
          }
        }

        // Update rich text display div if field is not focused
        const richDiv = (el as any)._richDisplayDiv as HTMLDivElement | undefined;
        if (richDiv && document.activeElement !== el) {
          const text = String(newValue);
          if (containsRichTextTags(text)) {
            const fieldConfig = (el as any)._fieldConfig || {};
            const { maxFontSize: mfs, minFontSize: minFs } = resolveAutoSizeBounds(fieldConfig, window.innerWidth <= 768);
            const rm = createCanvasRichTextMeasurer(EDITOR_FONT_FAMILY);
            const segments = parseRichText(text);
            const result = calculateFormattedAutoSizeFontSize({
              segments,
              maxWidth: parseFloat(el.style.width) || 100,
              maxHeight: parseFloat(el.style.height) || 20,
              maxFontSize: mfs,
              minFontSize: minFs,
              measurer: rm
            });
            richDiv.style.fontSize = `${result.baseFontSize}px`;
            richDiv.innerHTML = renderRichTextToHTML(segments, result.baseFontSize);
            richDiv.style.display = 'block';
            el.style.display = 'none';
          } else {
            richDiv.style.display = 'none';
            el.style.display = 'block';
          }
        }
      }
    });
  }, [currentFieldValues]);

  // Manage Load Default buttons when userDataFields changes (e.g., after async fetch)
  useEffect(() => {
    console.log(`[LoadDefault] useEffect triggered: wrapperRef=${!!wrapperRef.current} form=${!!form} userDataFields count=${userDataFields?.length ?? 'undefined'}`);
    if (!wrapperRef.current || !form) {
      console.log(`[LoadDefault] useEffect early return: wrapperRef=${!!wrapperRef.current} form=${!!form}`);
      return;
    }

    const annotationLayer = wrapperRef.current.querySelector('.annotationLayer');
    if (!annotationLayer) {
      console.log('[LoadDefault] useEffect early return: no .annotationLayer found');
      return;
    }

    // Remove all existing Load Default buttons
    const existingBtns = annotationLayer.querySelectorAll('[data-load-default-btn]');
    console.log(`[LoadDefault] useEffect: removing ${existingBtns.length} existing buttons`);
    existingBtns.forEach(btn => btn.remove());

    if (!userDataFields || userDataFields.length === 0) {
      console.log(`[LoadDefault] useEffect early return: userDataFields empty or undefined`);
      return;
    }

    console.log(`[LoadDefault] useEffect: userDataFields=`, userDataFields.map(f => ({ code: f.businessDataField?.code, value: f.value })));

    // Iterate all field elements and create Load Default buttons where appropriate
    const fieldElements = annotationLayer.querySelectorAll('[data-field-name]');
    console.log(`[LoadDefault] useEffect: found ${fieldElements.length} field elements in annotation layer`);
    fieldElements.forEach(el => {
      const fieldName = el.getAttribute('data-field-name');
      if (!fieldName) return;

      // Look up form config for this field
      let userDataProperty: string | null = null;
      for (const section of form.formSections || []) {
        const found = section.formFields?.find(f => f.code === fieldName);
        if (found) {
          try {
            const config = found.configurationJson ? JSON.parse(found.configurationJson) : {};
            if (config?.parameterValue?.source === 'UserDataField') {
              userDataProperty = config.parameterValue.property;
              console.log(`[LoadDefault] useEffect: field="${fieldName}" mapped to userDataProperty="${userDataProperty}"`);
            }
          } catch (e) { }
          break;
        }
      }

      if (!userDataProperty) return;

      const matchingVal = userDataFields.find((f: UserDataFieldDto) => f.businessDataField?.code === userDataProperty)?.value;
      console.log(`[LoadDefault] useEffect: field="${fieldName}" userDataProperty="${userDataProperty}" matchingVal=`, matchingVal);
      if (matchingVal === undefined || matchingVal === null) return;

      // Read positioning from data attributes
      const left = parseFloat(el.getAttribute('data-field-left') || '0');
      const top = parseFloat(el.getAttribute('data-field-top') || '0');
      const fieldWidth = parseFloat(el.getAttribute('data-field-width') || '0');
      const isCheckbox = el.getAttribute('data-field-is-checkbox') === 'true';

      const loadBtn = document.createElement('button');
      loadBtn.setAttribute('data-load-default-btn', fieldName);
      loadBtn.innerHTML = 'Load Default';
      loadBtn.title = `Load Default: ${matchingVal}`;
      loadBtn.style.position = 'absolute';
      const btnLeft = isCheckbox ? left + fieldWidth + 10 : left + fieldWidth + 10 + 30;
      loadBtn.style.left = `${btnLeft}px`;
      loadBtn.style.top = `${top}px`;
      loadBtn.style.padding = '12px 15px';
      loadBtn.style.fontSize = '12px';
      loadBtn.style.whiteSpace = 'nowrap';
      loadBtn.style.cursor = 'pointer';
      loadBtn.style.zIndex = '11';
      loadBtn.style.backgroundColor = '#f0f0f0';
      loadBtn.style.border = '1px solid #ccc';
      loadBtn.style.borderRadius = '3px';

      loadBtn.onclick = (e) => {
        e.preventDefault();
        let valToSet: any = matchingVal;
        if (isCheckbox) {
          valToSet = matchingVal === 'true' || matchingVal === 'True' || matchingVal === 'Yes' || matchingVal === 'On';
        }
        onFieldChange(fieldName, valToSet);
      };

      console.log(`[LoadDefault] useEffect: CREATED button for field="${fieldName}" value="${matchingVal}" at left=${btnLeft} top=${top}`);
      annotationLayer.appendChild(loadBtn);
    });
  }, [userDataFields, form, scale, annotationLayerReady]);

  const handleDateTimePickerChange = (newValue: Date | null) => {
    if (dateTimePickerState.fieldName && newValue) {
      // Format as local ISO string (without Z) to preserve local time
      const localIso = formatDate(newValue, "yyyy-MM-dd'T'HH:mm:ss");
      onFieldChange(dateTimePickerState.fieldName, localIso);

      // Update the display value in the input field
      const inputEl = wrapperRef.current?.querySelector(
        `input[data-field-name="${dateTimePickerState.fieldName}"]`
      ) as HTMLInputElement;
      if (inputEl) {
        inputEl.value = formatDate(newValue, 'dd/MM/yyyy hh:mm a', { locale: enGB });
      }

      // Keep picker open, just update the value
      setDateTimePickerState(prev => ({ ...prev, value: newValue }));
    }
  };

  const handleDateTimePickerClose = () => {
    // Save on close
    if (dateTimePickerState.fieldName && dateTimePickerState.value) {
      const localIso = formatDate(dateTimePickerState.value, "yyyy-MM-dd'T'HH:mm:ss");
      onFieldBlur(dateTimePickerState.fieldName, localIso);
    }
    setDateTimePickerState({ open: false, fieldName: null, value: null, anchorEl: null });
  };

  const handleDatePickerChange = (newValue: Date | null) => {
    if (datePickerState.fieldName && newValue) {
      // Format as local ISO date string
      const localIso = formatDate(newValue, 'yyyy-MM-dd');
      onFieldChange(datePickerState.fieldName, localIso);
      onFieldBlur(datePickerState.fieldName, localIso);

      // Update the display value in the input field
      const inputEl = wrapperRef.current?.querySelector(
        `input[data-field-name="${datePickerState.fieldName}"]`
      ) as HTMLInputElement;
      if (inputEl) {
        inputEl.value = formatDate(newValue, 'dd/MM/yyyy', { locale: enGB });
      }
    }
    // Close picker after date selection
    setDatePickerState({ open: false, fieldName: null, value: null, anchorEl: null });
  };

  const handleDatePickerClose = () => {
    setDatePickerState({ open: false, fieldName: null, value: null, anchorEl: null });
  };

  return (
    <div
      ref={wrapperRef}
      style={{
        position: 'relative',
        marginBottom: '20px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        backgroundColor: 'white',
        isolation: 'isolate',
        contain: 'layout',
        transform: 'translateZ(0)'
      }}
    >
      <canvas ref={canvasRef} style={{ display: 'block' }} />

      {/* MUI DateTimePicker for datetime fields with 15-minute steps */}
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
        <DateTimePicker
          open={dateTimePickerState.open}
          onClose={handleDateTimePickerClose}
          value={dateTimePickerState.value}
          onChange={handleDateTimePickerChange}
          closeOnSelect={false}
          ampm={true}
          timeSteps={{ hours: 1, minutes: 15 }}
          slotProps={{
            textField: {
              sx: { display: 'none' }
            },
            popper: {
              anchorEl: dateTimePickerState.anchorEl,
              placement: 'bottom-start'
            }
          }}
        />

        {/* MUI DatePicker for date-only fields */}
        <DatePicker
          open={datePickerState.open}
          onClose={handleDatePickerClose}
          value={datePickerState.value}
          onChange={handleDatePickerChange}
          slotProps={{
            textField: {
              sx: { display: 'none' }
            },
            popper: {
              anchorEl: datePickerState.anchorEl,
              placement: 'bottom-start'
            }
          }}
        />
      </LocalizationProvider>
    </div>
  );
};

const MemoizedPDFPageComponent = React.memo(PDFPageComponent);
export default MemoizedPDFPageComponent;
